<!--
  DistributorOrderDetail -- Distributor App per-order detail page.

  Card: Pricing & Splits -- Reporting & Insights (C)
  (https://trello.com/c/pSDstQD3).
  Pebble: Frontend: DistributorOrderDetail page with OrderDetailView +
  OrderProfitBreakdown.

  Reached from DistributorOrdersList by clicking a row; the route param
  :orderId arrives as a prop (props:true on the route). Fetches the order via
  GET /api/orders/:id (SAFRS, tenant-gated -- an actor can only load orders in
  their own tenant), maps the JSON:API payload to the CLIENT-SAFE shape that
  OrderDetailView renders, and mounts the DISTRIBUTOR-INTERNAL
  OrderProfitBreakdown in OrderDetailView's #internal slot (it self-fetches its
  split by :order-id).

  Separation of concerns (Jake, 2026-06-30): OrderDetailView is client-safe by
  construction (no cost/margin fields); the profit/cost/margin decomposition
  lives ONLY in OrderProfitBreakdown, mounted here because this is a
  distributor-context surface. A 404 (missing / cross-tenant order) renders an
  error card with the Back link still reachable.

  Batch + shipping (Jake, 2026-10-06): a second read,
  GET /api/reporting/distributor-orders/:id/fulfillment, supplies the batch
  the order is in and its frozen ship-to -- the same address the store's
  Batches page shows. The batch block lives HERE, outside the shared
  OrderDetailView, because which batch an order rides is a distributor
  concern; the shipping block feeds OrderDetailView's existing `shipping`
  section. A failed read never claims "not in a batch" or "no shipping
  details on file" -- it says the batch and shipping could not be loaded.
-->
<template>
  <section class="dodetail" data-testid="dist-order-detail-page">
    <div class="dodetail__nav">
      <router-link
        to="/distributor/reporting/orders"
        class="dodetail__back"
        data-testid="dist-order-detail-back"
      >
        &larr; Back to orders
      </router-link>
    </div>

    <div
      v-if="loading"
      class="dodetail__loading"
      data-testid="dist-order-detail-loading"
      role="status"
    >
      Loading order...
    </div>

    <div
      v-else-if="loadError || !order"
      class="dodetail__error"
      data-testid="dist-order-detail-error"
      role="alert"
    >
      <p class="dodetail__error-msg">
        {{ notFound
          ? "We could not find that order. It may have been removed, or it belongs to another distributor."
          : "We could not load this order. Please try again." }}
      </p>
    </div>

    <template v-else>
      <div class="dodetail__batch" data-testid="dist-order-detail-batch">
        <span class="dodetail__batch-label">Batch</span>
        <span
          v-if="fulfillmentError"
          class="dodetail__batch-muted"
          data-testid="dist-order-detail-batch-error"
        >
          Batch details could not be loaded.
        </span>
        <template v-else-if="batch">
          <router-link
            v-if="batchesRoute"
            :to="batchesRoute"
            class="dodetail__batch-link"
            data-testid="dist-order-detail-batch-link"
          >
            {{ batch.label }}
          </router-link>
          <span v-else data-testid="dist-order-detail-batch-link">{{
            batch.label
          }}</span>
          <BatchStatusChip
            :status="batch.status"
            gloss
            testid="dist-order-detail-batch-status"
          />
        </template>
        <span
          v-else
          class="dodetail__batch-muted"
          data-testid="dist-order-detail-batch-none"
        >Not yet in a batch</span>
      </div>

      <div class="dodetail__view" data-testid="dist-order-detail-view">
        <OrderDetailView
          :order="order"
          :funding="priceBreakdownBands"
          funding-label="Price breakdown"
          :payment-label="paymentLabel"
          :shipping-unavailable="fulfillmentError"
        >
          <template #internal>
            <div
              class="dodetail__profit"
              data-testid="dist-order-detail-profit"
            >
              <OrderProfitBreakdown :order-id="orderId" />
            </div>
          </template>
        </OrderDetailView>
      </div>
    </template>
  </section>
</template>

<script>
import OrderDetailView from "@/components/Order/OrderDetailView.vue";
import OrderProfitBreakdown from "@/components/Order/OrderProfitBreakdown.vue";
import BatchStatusChip from "@/pages/distributor/store-editor/StoreBatches/BatchStatusChip.vue";
import { getDistributorOrderFulfillment } from "@/services/distributorService";
import { mapOrderForDetailView, mapOrderBatch } from "./orderDetailMapper";
import { fetchOrderTotals, totalsToBands } from "@/components/Order/orderTotals";

export default {
  name: "DistributorOrderDetail",
  components: { OrderDetailView, OrderProfitBreakdown, BatchStatusChip },
  props: {
    // Route param, passed via props:true. String on the wire; forwarded to
    // OrderProfitBreakdown (which accepts Number|String).
    orderId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      order: null,
      loading: true,
      loadError: false,
      notFound: false,
      // The batch the order is in (mapOrderBatch), null when it is in none.
      batch: null,
      // The fulfillment read failed: the batch is UNKNOWN, which is not the
      // same as "not in a batch" and must not render as it.
      fulfillmentError: false,
      // THE shopper-facing breakdown (GET /orders/<id>/totals, Bug Intake
      // #481): the same lines and Total the shopper saw. Null when the read
      // failed; the page then shows the Total alone, as before.
      totals: null,
      // Monotonic request id -- guards against out-of-order responses when the
      // orderId watcher re-fetches while a prior fetch is still in flight
      // (e.g. deep-link navigation directly between two detail URLs). Only the
      // latest request may write state, so a slow stale response can never
      // overwrite a newer order's body while the URL + the child
      // OrderProfitBreakdown (which forwards the live orderId and has its own
      // reqId guard) already show the newer order. Mirrors the sibling
      // OrderProfitBreakdown.vue guard so the two stay consistent.
      reqId: 0,
    };
  },
  computed: {
    priceBreakdownBands() {
      return totalsToBands(this.totals);
    },
    // How the order was paid, from the frozen breakdown ("Net Checkout",
    // "Card", "Payroll deduction or invoice"); empty when it cannot be read.
    paymentLabel() {
      return (this.totals && this.totals.payment_label) || "";
    },
    batchesRoute() {
      // batches.store_id is nullable in the schema; never link to "null".
      return this.batch && this.batch.storeId != null
        ? `/distributor/stores/${this.batch.storeId}/edit/batches`
        : null;
    },
  },
  watch: {
    // Re-fetch if the route param changes while the component is reused
    // (e.g. deep-link navigation between two detail URLs).
    orderId() {
      this.loadOrder();
    },
  },
  created() {
    this.loadOrder();
  },
  methods: {
    async loadOrder() {
      const reqId = ++this.reqId; // claim the latest request
      this.loading = true;
      this.loadError = false;
      this.notFound = false;
      this.order = null;
      this.batch = null;
      this.fulfillmentError = false;
      this.totals = null;
      try {
        // Fetch the order through jsonapi-vuex (jv/get), NOT raw $axios: the
        // store denormalizes the JSON:API envelope (data + included[]) into a
        // flat object, so related resources resolve rather than sitting
        // unread in `included[]`. The endpoint is tenant-gated server-side.
        //
        // The second array element (the axios-config object) is REQUIRED, even
        // when empty. jsonapi-vuex 5.13.2's `get` action runs
        // `const [data, config] = utils.unpackArgs(args)`, and `unpackArgs`
        // returns an array arg AS-IS (it only substitutes a `{}` default for
        // NON-array args). So a single-element array leaves `config` undefined,
        // and the next line `config['data'] = config['data'] || {}` throws
        // `TypeError: reading 'data'` BEFORE issuing any XHR (the order request
        // never reaches the network). Passing `{}` (mirrors the sibling
        // order_items / StorefrontOrderDetail calls, which always
        // pass a config) keeps the call on the working path.
        const jvOrder = await this.$store.dispatch("jv/get", [
          `/orders/${this.orderId}`,
          {},
        ]);

        if (reqId !== this.reqId) return; // superseded by a newer fetch

        // jv/get resolves to {} (not null) for a resource it could not load;
        // treat an id-less result as not-found.
        if (!jvOrder || !jvOrder._jv || jvOrder._jv.id == null) {
          this.notFound = true;
          this.loadError = true;
          return;
        }

        // The three follow-up reads are independent of one another, so they
        // run in PARALLEL; each is best effort and settles to a fallback
        // rather than rejecting, so one failure never blanks the page.
        //
        // Line items (with campaign_product for product names) come from a
        // separate include -- mirrors StorefrontOrderDetail / ResponsiveOrderView.
        // A failure renders the order header/total with an empty items list.
        const itemsRead = this.$store
          .dispatch("jv/get", [
            `/orders/${this.orderId}/order_items`,
            { params: { include: "campaign_product" } },
          ])
          .then((jvItems) => Object.values(jvItems || {}))
          .catch(() => []);

        // Store name: the Order carries store_id but no jv-exposed store
        // relationship, so fetch the store by id for its display name. A
        // failure just leaves the store label blank. Config object required
        // -- see the order fetch above for why an empty `{}` second element
        // is mandatory (jv/get crashes on an undefined config before making
        // the request).
        // SAFRS exposes the order's store as `campaign_id`; there is NO
        // `store_id` attribute on the wire, so reading that left the Store
        // field "--" on every order (card #400).
        const storeId = jvOrder.campaign_id;
        const storeNameRead =
          storeId == null
            ? Promise.resolve(null)
            : this.$store
                .dispatch("jv/get", [`/stores/${storeId}`, {}])
                .then((jvStore) => (jvStore && jvStore.name) || null)
                .catch(() => null);

        // Batch + shipping. A failure keeps the order on screen and marks
        // the batch unknown (`fulfillmentError`), never "not in a batch".
        const fulfillmentRead = getDistributorOrderFulfillment(this.orderId)
          .then((res) => ({ body: (res && res.data) || null, failed: false }))
          .catch(() => ({ body: null, failed: true }));

        // The shopper-facing breakdown. Best effort: null on failure.
        const totalsRead = fetchOrderTotals(this.$axios, this.orderId);

        const [items, storeName, fulfillmentResult, totals] = await Promise.all([
          itemsRead,
          storeNameRead,
          fulfillmentRead,
          totalsRead,
        ]);
        if (reqId !== this.reqId) return; // superseded by a newer fetch

        const fulfillment = fulfillmentResult.body;
        this.fulfillmentError = fulfillmentResult.failed;
        this.batch = mapOrderBatch(fulfillment);
        this.totals = totals;
        this.order = mapOrderForDetailView(
          jvOrder,
          items,
          storeName,
          fulfillment,
        );
      } catch (e) {
        if (reqId !== this.reqId) return; // stale failure; ignore
        const status = e && e.response ? e.response.status : null;
        if (status === 404 || status === 403) {
          this.notFound = true;
        }
        this.loadError = true;
      } finally {
        // Only the latest request clears the spinner, so an early-returning
        // stale call cannot flip loading off while the current fetch runs.
        if (reqId === this.reqId) this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.dodetail {
  max-width: 720px;
  margin: var(--tf-sp-8, 32px) auto;
  padding: 0 var(--tf-sp-4, 16px);
  color: var(--tf-fg-2, rgba(0, 0, 0, 0.75));
}
.dodetail__nav {
  margin-bottom: var(--tf-sp-4, 16px);
}
.dodetail__back {
  color: var(--tf-link, #1565c0);
  text-decoration: none;
  font-size: var(--tf-size-body-sm, 0.875rem);
}
.dodetail__back:hover {
  text-decoration: underline;
}
.dodetail__loading {
  font-style: italic;
  padding: var(--tf-sp-6, 24px) 0;
}
.dodetail__error {
  padding: var(--tf-sp-6, 24px);
  border: 1px solid var(--tf-border, rgba(0, 0, 0, 0.12));
  border-radius: var(--tf-radius-md, 12px);
  background: var(--tf-surface, #fff);
}
.dodetail__error-msg {
  color: var(--tf-fg-2, rgba(0, 0, 0, 0.7));
}
.dodetail__batch {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--tf-sp-2, 8px) var(--tf-sp-3, 12px);
  margin-bottom: var(--tf-sp-4, 16px);
  font-size: var(--tf-size-body-sm, 0.875rem);
}
.dodetail__batch-label {
  font-weight: 600;
}
.dodetail__batch-link {
  color: var(--tf-link, #1565c0);
  text-decoration: none;
  font-weight: 500;
}
.dodetail__batch-link:hover {
  text-decoration: underline;
}
.dodetail__batch-muted {
  color: var(--tf-fg-3, rgba(0, 0, 0, 0.6));
}
.dodetail__profit {
  margin-top: var(--tf-sp-6, 24px);
}
</style>
